import React from 'react';
import { useRouter } from 'next/router'

import {
  SortOrder,
  Transaction,
  QueryTransactionsOrderByColumn,
  TransactionStatus,
  useListTransactionsQuery,
} from '@/generated/graphql';
import { calculateCurrentYield, calculatePortfolioWeight } from '@/helpers/transactions';
import { Currency } from '@/helpers/currency';
import { getTransactionsJournalStats } from '@/components/transactions-journal/useTransactionStats';
import { Headline } from '@/components/typography';
import { Stats } from '@/components/stats';

import { TransactionCard } from './components/transaction-card';

export const TransactionCardsList: React.FC = () => {
  const router = useRouter();
  const { loading, error, data } = useListTransactionsQuery({
    variables: {
      status: TransactionStatus.Open,
      orderBy: [{ column: QueryTransactionsOrderByColumn.BuyDate, order: SortOrder.Asc }],
    },
  });
  const transactions = data?.transactions as Transaction[];

  const openStats = React.useMemo(() => getTransactionsJournalStats({ transactions }), [transactions]);

  if (loading || error || !transactions?.length) return null;

  return (
    <section className="mb-12" aria-label="Operaciones abiertas">
      <Headline title="Operaciones abiertas" />
      <Stats
        items={[
          { title: 'Hoy', value: openStats.todayReturns, highlight: openStats.todayReturnsYield },
          { title: 'Rendimiento', value: openStats.totalReturns, highlight: openStats.totalReturnsYield },
          { title: 'Valor de mercado', value: openStats.totalValue, description: `coste ${openStats.totalCost}` },
        ]}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 lg:max-w-none">
        {transactions.map(({ id, buyPrice, stock, shares, portfolio, acquisitionValue }) => {
          // @ts-ignore
          const { euroReturns, euroYield, currencyReturns, currencyYield } = calculateCurrentYield({
            acquisitionValue,
            buyPrice,
            stock,
            shares,
          });

          return (
            <TransactionCard
              key={`transaction-card-${id}`}
              currentPrice={stock.value}
              lastValueAt={stock.lastValueAt}
              lastChange={stock.change}
              lastChangePercent={stock.changePercent}
              returnsInPortfolioCurrency={euroReturns}
              yieldInPortfolioCurrency={euroYield}
              returnsInStockCurrency={currencyReturns}
              yieldInStockCurrency={currencyYield}
              buyPrice={buyPrice}
              stock={stock.name}
              stockCurrency={stock.market.currency as Currency}
              ticker={stock.symbol}
              market={stock.market.symbol}
              shares={shares}
              portfolio={{ id: portfolio.id, name: portfolio.name, currency: portfolio.currency as Currency }}
              portfolioWeight={calculatePortfolioWeight(acquisitionValue, portfolio.cost)}
              onCardClick={() => router.push(`/transactions/${id}`)}
            />
          );
        })}
      </div>
    </section>
  );
};
